/**
 * MeetingOverviewEnrichment — Enriched overview rows for meetings.
 *
 * Renders derived data that doesn't come from direct meeting properties:
 * - Active deals (fetched via the company associated with the meeting)
 * - Aggregate deal value
 * - Company size (headcount range from company properties)
 * - Deal stage (from first active deal)
 *
 * Self-contained: manages its own data fetching via React Query.
 * Renders using the same row layout pattern as OverviewSection fields.
 */

import { useQuery } from '@tanstack/react-query';
import { CurrencyDollar, Flag01, Users01 } from '@untitledui/icons';
import { useCallback, useMemo } from 'react';

import { Skeleton } from '@/lib/base/skeleton/skeleton';
import type { CrmObject } from '@/types/crm';
import { getPropertyValue } from '@/types/crm';

import { EntityBadgeGroup } from '../../components/badges/EntityBadgeGroup';
import type { EntityBadgeData } from '../../components/badges/types';
import { useAssociations } from '../../hooks/useAssociations';
import { useOpenSlideoutOptional } from '../../hooks/useOpenSlideout';
import { usePipelineStages } from '../../hooks/usePipelineStages';
import { crmObjectListSentinelQuery, useStoreBackedList } from '../../store';
import { getDisplayName } from '../../utils/getDisplayName';
import { getStageEntityId } from '../../utils/propertyHelpers';
import { OverviewFieldRow } from '../components/OverviewFieldRow';

// ============================================================================
// Main component
// ============================================================================

interface MeetingOverviewEnrichmentProps {
  object: CrmObject;
}

export function MeetingOverviewEnrichment({
  object,
}: MeetingOverviewEnrichmentProps) {
  const orgId = object.org_id;

  // Resolve associations to find the company
  const associations = useMemo(
    () => object.associations ?? [],
    [object.associations]
  );
  const { associations: resolvedAssociations } = useAssociations(
    orgId,
    associations,
    { includeAssociations: false }
  );

  const companyAssociation = useMemo(
    () =>
      resolvedAssociations.find(
        a => a.association_type_name === 'ahoy_meeting_to_company' && a.object
      ),
    [resolvedAssociations]
  );

  const companyObject = companyAssociation?.object ?? null;
  const companyEntityId = companyObject?.entity_id;

  // Fetch deals associated with the company. Memoized like the sibling
  // deal queries (useMeetingDeals, deal-status-section) — rebuilding the
  // options object every render creates unstable references for anything
  // consuming it downstream, even though React Query hashes the key
  // structurally.
  const dealsQueryOptions = useMemo(
    () =>
      crmObjectListSentinelQuery(
        orgId,
        'ahoy_deal',
        {
          associated_with_entity_id: companyEntityId,
          properties: ['ahoy_name', 'ahoy_amount'],
        },
        { include_associated_objects: true }
      ),
    [orgId, companyEntityId]
  );
  const dealsQuery = useQuery({
    ...dealsQueryOptions,
    enabled: !!companyEntityId,
  });
  const list = useStoreBackedList(dealsQuery);

  // Stage helpers for filtering terminal deals
  const { getStageByEntityId, stageTypeNames } = usePipelineStages(
    orgId,
    'ahoy_deal'
  );

  // Filter to active (non-terminal) deals only
  const activeDeals = useMemo(() => {
    return list.results.filter(deal => {
      const stageEntityId = getStageEntityId(deal, stageTypeNames);
      if (!stageEntityId) {
        return true;
      }
      const stage = getStageByEntityId(stageEntityId);
      if (!stage) {
        return true;
      }
      return stage.probability !== 0 && stage.probability !== 1;
    });
  }, [list.results, getStageByEntityId, stageTypeNames]);

  // Aggregate deal value from active deals
  const totalDealValue = useMemo(() => {
    let total = 0;
    for (const deal of activeDeals) {
      const raw = getPropertyValue<{
        amount: string | number | null;
        unit: string | null;
      }>(deal.properties?.ahoy_amount);
      if (raw && typeof raw === 'object' && raw.amount != null) {
        const num =
          typeof raw.amount === 'number' ? raw.amount : Number(raw.amount);
        if (isFinite(num)) {
          total += num;
        }
      }
    }
    return total > 0 ? total : null;
  }, [activeDeals]);

  // Stage label from first active deal
  const dealStageLabel = useMemo(() => {
    if (activeDeals.length === 0) {
      return null;
    }
    const stageEntityId = getStageEntityId(activeDeals[0], stageTypeNames);
    if (!stageEntityId) {
      return null;
    }
    return getStageByEntityId(stageEntityId)?.label ?? null;
  }, [activeDeals, getStageByEntityId, stageTypeNames]);

  // Build EntityBadgeGroup data directly from already-fetched deals.
  // No need to round-trip through EntityBadgeByIds since we already have the
  // full CrmObject for each deal.
  const dealBadgeData: EntityBadgeData[] = useMemo(
    () =>
      activeDeals.map(deal => ({
        id: deal.entity_id,
        entityType: deal.entity_type_name,
        displayName: getDisplayName(deal),
      })),
    [activeDeals]
  );

  const openSlideout = useOpenSlideoutOptional();
  const handleDealClick = useCallback(
    (id: string) => {
      openSlideout?.(id);
    },
    [openSlideout]
  );

  // Company size from company properties
  const companySize = companyObject
    ? getPropertyValue<string>(companyObject.properties?.ahoy_headcount_range)
    : null;

  // Only render if there's enriched data to show
  const hasContent =
    activeDeals.length > 0 ||
    totalDealValue !== null ||
    companySize ||
    dealStageLabel;

  // While deal bodies are still resolving from the store, show a loading row
  // instead of a blank one — the deals may not have any active ones once
  // fully resolved, but we shouldn't render nothing while that's unknown.
  if (!hasContent && list.isLoading) {
    return (
      <OverviewFieldRow
        icon={CurrencyDollar}
        label='Deals'
        valueClassName='flex flex-wrap gap-1'
      >
        <Skeleton variant='rectangular' width={60} height={20} />
      </OverviewFieldRow>
    );
  }

  if (!hasContent) {
    return null;
  }

  return (
    <>
      {/* Company size */}
      {companySize && (
        <OverviewFieldRow
          icon={Users01}
          label='Company size'
          valueClassName='text-xs md:text-sm font-normal text-fg-primary'
        >
          {companySize}
        </OverviewFieldRow>
      )}

      {/* Active deals (fetched via company) */}
      {activeDeals.length > 0 && (
        <OverviewFieldRow
          icon={CurrencyDollar}
          label='Deals'
          valueClassName='flex flex-wrap gap-1'
        >
          <EntityBadgeGroup
            state={{ kind: 'data', data: dealBadgeData }}
            variant='xs'
            onClick={openSlideout ? handleDealClick : undefined}
          />
        </OverviewFieldRow>
      )}

      {/* Stage (from first active deal) */}
      {dealStageLabel && (
        <OverviewFieldRow
          icon={Flag01}
          label='Stage'
          valueClassName='text-xs md:text-sm font-normal text-fg-primary'
        >
          {dealStageLabel}
        </OverviewFieldRow>
      )}

      {/* Deal value (aggregate across active deals) */}
      {totalDealValue !== null && (
        <OverviewFieldRow
          icon={CurrencyDollar}
          label='Deal value'
          valueClassName='text-xs md:text-sm font-normal text-fg-primary'
        >
          $
          {totalDealValue.toLocaleString(undefined, {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          })}
        </OverviewFieldRow>
      )}
    </>
  );
}
